import React, { createContext, useContext, useCallback } from 'react';
import { useApiAuth } from './ApiAuthContext';
import { activityLogService, ACTIVITY_TYPES } from '../services/activityLogService';
import { activitiesService } from '../features/activities/api/activitiesApi';

const ActivityLogContext = createContext();

export const useActivityLog = () => {
  const context = useContext(ActivityLogContext);
  if (!context) {
    throw new Error('useActivityLog must be used within an ActivityLogProvider');
  }
  return context;
};

export const ActivityLogProvider = ({ children }) => {
  const { user } = useApiAuth();

  const logActivity = useCallback(
    async (type, details, projectId = null) => {
      if (!user) return null;
      return await activityLogService.logActivity(user.id || user.uid, type, details, projectId);
    },
    [user]
  );

  const logTaskActivity = useCallback(
    async (action, task, projectId) => {
      if (!user || !task) return null;

      let activityType;
      switch (action) {
        case 'create':
          activityType = ACTIVITY_TYPES.TASK_CREATED;
          break;
        case 'update':
          activityType = ACTIVITY_TYPES.TASK_UPDATED;
          break;
        case 'complete':
          activityType = ACTIVITY_TYPES.TASK_COMPLETED;
          break;
        case 'delete':
          activityType = ACTIVITY_TYPES.TASK_DELETED;
          break;
        default:
          return null;
      }

      const details = {
        taskId: task.id,
        title: task.title,
        status: task.status,
        action
      };

      return await logActivity(activityType, details, projectId);
    },
    [user, logActivity]
  );

  const logProjectActivity = useCallback(
    async (action, project) => {
      if (!user || !project) return null;

      let activityType;
      switch (action) {
        case 'create':
          activityType = ACTIVITY_TYPES.PROJECT_CREATED;
          break;
        case 'update':
          activityType = ACTIVITY_TYPES.PROJECT_UPDATED;
          break;
        case 'delete':
          activityType = ACTIVITY_TYPES.PROJECT_DELETED;
          break;
        default:
          return null;
      }

      const details = {
        projectId: project.id,
        name: project.name,
        action
      };

      return await logActivity(activityType, details, project.id);
    },
    [user, logActivity]
  );

  const logMilestoneActivity = useCallback(
    async (action, milestone, projectId) => {
      if (!user || !milestone || !projectId) return null;

      let activityType;
      switch (action) {
        case 'create':
          activityType = ACTIVITY_TYPES.MILESTONE_CREATED;
          break;
        case 'update':
          activityType = ACTIVITY_TYPES.MILESTONE_UPDATED;
          break;
        case 'complete':
          activityType = ACTIVITY_TYPES.MILESTONE_COMPLETED;
          break;
        default:
          return null;
      }

      const details = {
        milestoneId: milestone.id,
        title: milestone.title,
        status: milestone.status,
        action
      };

      return await logActivity(activityType, details, projectId);
    },
    [user, logActivity]
  );

  const logDocumentActivity = useCallback(
    async (action, document, projectId) => {
      if (!user || !document) return null;

      let activityType;
      switch (action) {
        case 'upload':
          activityType = ACTIVITY_TYPES.DOCUMENT_UPLOADED;
          break;
        case 'update':
          activityType = ACTIVITY_TYPES.DOCUMENT_UPDATED;
          break;
        case 'delete':
          activityType = ACTIVITY_TYPES.DOCUMENT_DELETED;
          break;
        default:
          return null;
      }

      const details = {
        documentId: document.id,
        name: document.name || document.title,
        type: document.type,
        action
      };

      return await logActivity(activityType, details, projectId);
    },
    [user, logActivity]
  );

  const logCommentActivity = useCallback(
    async (comment, projectId, relatedItemType, relatedItemId) => {
      if (!user || !comment) return null;

      const details = {
        commentId: comment.id,
        content: comment.content,
        relatedItemType,
        relatedItemId
      };

      return await logActivity(ACTIVITY_TYPES.COMMENT_ADDED, details, projectId);
    },
    [user, logActivity]
  );

  const logUserSessionActivity = useCallback(
    async (action) => {
      if (!user) return null;

      const activityType = action === 'login' 
        ? ACTIVITY_TYPES.USER_LOGIN 
        : ACTIVITY_TYPES.USER_LOGOUT;

      const details = {
        timestamp: new Date().toISOString(),
        action
      };

      return await logActivity(activityType, details);
    },
    [user, logActivity]
  );

  const logPageVisit = useCallback(
    async (pathname) => {
      if (!user) return null;

      const details = {
        timestamp: new Date().toISOString(),
        pathname
      };

      return await logActivity(ACTIVITY_TYPES.PAGE_VISIT, details);
    },
    [user, logActivity]
  );

  const logResourceActivity = useCallback(
    async (action, resource, projectId) => {
      if (!user || !resource) return null;

      const activityType = action === 'allocate' 
        ? ACTIVITY_TYPES.RESOURCE_ALLOCATED 
        : ACTIVITY_TYPES.RESOURCE_DEALLOCATED;

      const details = {
        resourceId: resource.id,
        name: resource.name,
        type: resource.type,
        action
      };

      return await logActivity(activityType, details, projectId);
    },
    [user, logActivity]
  );

  const getRecentActivities = useCallback(async (limit = 50) => {
    try { return await activitiesService.getRecentActivities(limit); }
    catch { return []; }
  }, []);

  const getUserActivities = useCallback(
    async (userId = null, limit = 50) => {
      const targetId = userId || user?.id || user?.uid;
      if (!targetId) return [];
      try {
        return userId
          ? await activitiesService.getUserActivities(targetId, limit)
          : await activitiesService.getMyActivities(limit);
      } catch { return []; }
    },
    [user]
  );

  const getProjectActivities = useCallback(async (projectId, limit = 100) => {
    if (!projectId) return [];
    try { return await activitiesService.getProjectActivities(projectId, limit); }
    catch { return []; }
  }, []);

  const getUserActivityMetrics = useCallback(async () => ({}), []);
  const getTeamActivitySummary = useCallback(async () => [], []);

  const value = {
    // Logging functions
    logActivity,
    logTaskActivity,
    logProjectActivity,
    logMilestoneActivity,
    logDocumentActivity,
    logCommentActivity,
    logUserSessionActivity,
    logPageVisit,
    logResourceActivity,

    // Retrieval functions
    getRecentActivities,
    getUserActivities,
    getProjectActivities,
    getUserActivityMetrics,
    getTeamActivitySummary,

    // Constants
    ACTIVITY_TYPES
  };

  return (
    <ActivityLogContext.Provider value={value}>
      {children}
    </ActivityLogContext.Provider>
  );
}; 